import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "C:/nexus/data-gh/guide/src/components/layout/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1>{`Data Wrangling and Manipulation in JS`}</h1>
    <p>{`This guide goes over 3 major concepts:`}</p>
    <ul>
      <li parentName="ul">{`Array Operations`}</li>
      <li parentName="ul">{`Javascript Objects`}</li>
      <li parentName="ul">{`Wrangling in D3`}</li>
    </ul>
    <h2>{`Array Operations`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const myArr = [1, 2, 3, 4, 5, 6, 7, 8, 9]
`}</code></pre>
    <h3>{`Map`}</h3>
    <p>{`Map applies a function to each element of an array and returns a new array of the function outputs.`}</p>
    <p>{`Use `}<inlineCode parentName="p">{`.map()`}</inlineCode>{` to return an array of the squares of `}<inlineCode parentName="p">{`myArr`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const squares = myArr.map(x => x**2);
console.log(squares); // [1, 4, 9, 16, 25, 36, 49, 64, 81]
`}</code></pre>
    <p><img parentName="p" {...{
        "src": "../../static/images/arrayMapExample.png",
        "alt": "test"
      }}></img></p>
    <p>{`We can also use more complex functions:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const factorials = myArr.map((x) => {
    let prod = 1;
    for (let i = 1; i <= x; ++i) {
        prod *= i;
    }
    return prod;
});
console.log(factorials); // [1, 2, 6, 24, 120, 720, 5040, 40320, 362880]
`}</code></pre>
    <p>{`The index of each element is passed in as the second item:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const indices = myArr.map((x, i) => i)
console.log(indices) // [0, 1, 2, 3, 4, 5, 6, 7, 8]
`}</code></pre>
    <h3>{`Filter`}</h3>
    <p>{`The filter operation returns an array of elements from the original array that meet a specified condition.`}</p>
    <p><inlineCode parentName="p">{`.filter()`}</inlineCode>{` returns the `}<inlineCode parentName="p">{`true`}</inlineCode>{` values from the elements passed through a function. The `}<inlineCode parentName="p">{`%`}</inlineCode>{` is the modulus operator which returns the remainder after division. When you divide an even number by 2, there will always be a remainder of zero. Get the even numbers of `}<inlineCode parentName="p">{`myArr`}</inlineCode>{` using `}<inlineCode parentName="p">{`.filter()`}</inlineCode>{`.`}</p>
    <p><img parentName="p" {...{
        "src": "../../static/images/arrayFilterExample.png",
        "alt": null
      }}></img></p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const evenNumbers = myArr.filter(x => x % 2 === 0);
console.log(evenNumbers); // [2, 4, 6, 8]
`}</code></pre>
    <h2>{`Javascript Objects`}</h2>
    <p>{`Objects are key-value collections of data.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const myObj = {
    name: "Labyrinth",
    nWriters: 5
};
`}</code></pre>
    <p>{`We can access items by their key:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`console.log(myObj.name); // "Labyrinth"
`}</code></pre>
    <h3>{`Object Destructuring`}</h3>
    <p>{`We can turn object values into individual variables as follows: `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const { name, nWriters } = myObj;
console.log(name, nWriters) // "Labyrinth", 5
`}</code></pre>
    <h2>{`Wrangling with D3`}</h2>
    <p>{`Oftentimes, our data will be arrays of objects. This array contains data for 3 students.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const students = [
    { major: "Math", gpa: 3.00 },
    { major: "Compsci", gpa: 3.50 },
    { major: "Compsci", gpa: 3.75 }
]
`}</code></pre>
    <h3>{`d3.max()`}</h3>
    <p>{`We can get the maximum gpa of all students:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { max } from "d3-array";

const maxGpa = max(students, d => d.gpa)
console.log(maxGpa) // 3.75
`}</code></pre>
    <p>{`The first argument passed into `}<inlineCode parentName="p">{`d3.max()`}</inlineCode>{` must be an array of values. Then, the second argument can be a function that returns a value for each element of the array.`}</p>
    <h3>{`d3.nest()`}</h3>
    <p>{`Often, we'll want to group data by a common value. Here, we're going to group together students with the same major using `}<inlineCode parentName="p">{`d3.nest()`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { nest } from "d3-collection";

const nestedData = nest()
    .key((d) => d.major)
    .entries(students)

console.log(nestedData)
// [
//   { 
//       key: 'Math', 
//       values: [ { major: 'Math', gpa: 3 } ],
//   },
//   { 
//       key: 'Compsci', 
//       values: [ 
//           { major: 'Compsci', gpa: 3.5 }, 
//           { major: 'Compsci', gpa: 3.75 } 
//       ] 
//   }
// ]
`}</code></pre>
    <p>{`Here, we pass a function through `}<inlineCode parentName="p">{`.key()`}</inlineCode>{` for the major value. This tells d3 to group the data by major. Then, we pass the entire array through by entering the array name into `}<inlineCode parentName="p">{`.entries()`}</inlineCode>{`.`}</p>
    <p>{`If we want to get the maximum GPA by major, we can map the `}<inlineCode parentName="p">{`nestedData`}</inlineCode>{` array and then use `}<inlineCode parentName="p">{`d3.max()`}</inlineCode>{` to get the maximum value from each major's `}<inlineCode parentName="p">{`values`}</inlineCode>{` key.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const maxGpaByDept = nestedData.map({ key, values }) => ({
    key,
    maxGPA: max(values, (d) => d.gpa),
}));

console.log(maxGpaByDept);
// [ { key: 'Math', maxGPA: 3 }, { key: 'Compsci', maxGPA: 3.75 } ]
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      